// DiningTables.jsx
import React, { useState, useEffect } from 'react';
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaUtensils,
  FaUsers,
  FaCircle,
  FaClock,
  FaCheckCircle,
  FaTimesCircle,
  FaSearch,
  FaChair,
  FaTable,
  FaTimes,
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

const DiningTables = () => {
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterSection, setFilterSection] = useState('all');
  const [sections, setSections] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingTable, setEditingTable] = useState(null);
  const [formData, setFormData] = useState({
    table_number: '',
    table_name: '',
    capacity: 4,
    status: 'available',
    section: '',
    is_active: true,
  });

  const navigate = useNavigate();

  // Status config with colors - Reserved: Red, Available: Green
  const statusConfig = {
    available: {
      bg: 'bg-white',
      border: 'border-green-500',
      icon: FaCheckCircle,
      label: 'Available',
      textColor: 'text-green-700',
      dotColor: 'bg-green-500',
      badgeBg: 'bg-green-100',
      badgeText: 'text-green-700',
    },
    occupied: {
      bg: 'bg-gray-100',
      border: 'border-gray-500',
      icon: FaTimesCircle,
      label: 'Occupied',
      textColor: 'text-gray-900',
      dotColor: 'bg-gray-700',
      badgeBg: 'bg-gray-100',
      badgeText: 'text-gray-700',
    },
    reserved: {
      bg: 'bg-white',
      border: 'border-red-500',
      icon: FaClock,
      label: 'Reserved',
      textColor: 'text-red-700',
      dotColor: 'bg-red-500',
      badgeBg: 'bg-red-100',
      badgeText: 'text-red-700',
    },
    out_of_service: {
      bg: 'bg-gray-50',
      border: 'border-gray-300',
      icon: FaTimesCircle,
      label: 'Out of Service',
      textColor: 'text-gray-500',
      dotColor: 'bg-gray-400',
      badgeBg: 'bg-gray-100',
      badgeText: 'text-gray-500',
    },
  };

  useEffect(() => {
    fetchTables();
  }, []);

  const fetchTables = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BQ_BASE_URL}/bq/api/dining-tables/`);
      const data = await response.json();
      if (response.ok) {
        setTables(data.tables || []);
        const uniqueSections = [...new Set(data.tables?.map((t) => t.section).filter((s) => s))];
        setSections(uniqueSections);
      } else {
        toast.error(data.error || 'Failed to fetch tables');
      }
    } catch (error) {
      console.error('Error fetching tables:', error);
      toast.error('Failed to load dining tables');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTable = async () => {
    if (!formData.table_number) {
      toast.error('Table number is required');
      return;
    }

    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/dining-tables/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      const data = await response.json();
      if (response.ok) {
        toast.success(data.message || 'Table created successfully');
        fetchTables();
        closeModal();
      } else {
        toast.error(data.error || 'Failed to create table');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to create table');
    }
  };

  const handleUpdateTable = async () => {
    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/dining-tables/${editingTable.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      const data = await response.json();
      if (response.ok) {
        toast.success(data.message || 'Table updated successfully');
        fetchTables();
        closeModal();
      } else {
        toast.error(data.error || 'Failed to update table');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to update table');
    }
  };

  const handleUpdateStatus = async (tableId, newStatus) => {
    // Get the table details for better toast message
    const table = tables.find((t) => t.id === tableId);
    const statusDisplay = {
      available: 'Available',
      occupied: 'Occupied',
      reserved: 'Reserved',
      out_of_service: 'Out of Service',
    };

    try {
      const response = await fetch(
        `${BQ_BASE_URL}/bq/api/dining-tables/${tableId}/status?new_status=${newStatus}`,
        {
          method: 'PATCH',
        }
      );
      const data = await response.json();
      if (response.ok) {
        // Show success toast with table info
        toast.success(
          `✅ ${table?.table_number || 'Table'} status updated to ${statusDisplay[newStatus]} successfully!`,
          {
            position: 'top-right',
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: 'light',
          }
        );
        fetchTables();
      } else {
        toast.error(data.error || 'Failed to update status');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to update status');
    }
  };

  const handleDeleteTable = async (tableId, tableNumber) => {
    if (window.confirm(`Are you sure you want to delete table ${tableNumber}?`)) {
      try {
        const response = await fetch(`${BQ_BASE_URL}/bq/api/dining-tables/${tableId}`, {
          method: 'DELETE',
        });
        if (response.ok) {
          toast.success(`🗑️ Table ${tableNumber} deleted successfully`);
          fetchTables();
        } else {
          const data = await response.json();
          toast.error(data.error || 'Failed to delete table');
        }
      } catch (error) {
        console.error('Error:', error);
        toast.error('Failed to delete table');
      }
    }
  };

  const openModal = (table = null) => {
    if (table) {
      setEditingTable(table);
      setFormData({
        table_number: table.table_number,
        table_name: table.table_name || '',
        capacity: table.capacity,
        status: table.status,
        section: table.section || '',
        is_active: table.is_active,
      });
    } else {
      setEditingTable(null);
      setFormData({
        table_number: '',
        table_name: '',
        capacity: 4,
        status: 'available',
        section: '',
        is_active: true,
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingTable(null);
  };

  const filteredTables = tables.filter((table) => {
    const matchesSearch =
      table.table_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (table.table_name && table.table_name.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesStatus = filterStatus === 'all' || table.status === filterStatus;
    const matchesSection = filterSection === 'all' || table.section === filterSection;
    return matchesSearch && matchesStatus && matchesSection;
  });

  const groupedTables = filteredTables.reduce((acc, table) => {
    const section = table.section || 'Unassigned';
    if (!acc[section]) acc[section] = [];
    acc[section].push(table);
    return acc;
  }, {});

  // Function to render chairs around the table based on capacity
  const renderChairs = (capacity, statusColor) => {
    const chairs = [];
    const maxChairs = Math.min(capacity, 12); // Limit to 12 chairs for visual clarity

    // Chair positions - top, right, bottom, left
    const positions = [];

    if (capacity === 1) {
      positions.push({ side: 'bottom', rotate: '' });
    } else if (capacity === 2) {
      positions.push({ side: 'left', rotate: '' });
      positions.push({ side: 'right', rotate: '' });
    } else if (capacity === 3) {
      positions.push({ side: 'top', rotate: '' });
      positions.push({ side: 'left', rotate: '' });
      positions.push({ side: 'right', rotate: '' });
    } else if (capacity === 4) {
      positions.push({ side: 'top', rotate: '' });
      positions.push({ side: 'right', rotate: '' });
      positions.push({ side: 'bottom', rotate: '' });
      positions.push({ side: 'left', rotate: '' });
    } else if (capacity === 5) {
      positions.push({ side: 'top', rotate: '' });
      positions.push({ side: 'top-right', rotate: 'rotate-45' });
      positions.push({ side: 'right', rotate: '' });
      positions.push({ side: 'bottom', rotate: '' });
      positions.push({ side: 'left', rotate: '' });
    } else if (capacity === 6) {
      positions.push({ side: 'top', rotate: '' });
      positions.push({ side: 'top-right', rotate: 'rotate-45' });
      positions.push({ side: 'right', rotate: '' });
      positions.push({ side: 'bottom-right', rotate: '-rotate-45' });
      positions.push({ side: 'bottom', rotate: '' });
      positions.push({ side: 'left', rotate: '' });
    } else if (capacity === 7) {
      positions.push({ side: 'top', rotate: '' });
      positions.push({ side: 'top-right', rotate: 'rotate-45' });
      positions.push({ side: 'right', rotate: '' });
      positions.push({ side: 'bottom-right', rotate: '-rotate-45' });
      positions.push({ side: 'bottom', rotate: '' });
      positions.push({ side: 'bottom-left', rotate: '-rotate-45' });
      positions.push({ side: 'left', rotate: '' });
    } else if (capacity === 8) {
      positions.push({ side: 'top', rotate: '' });
      positions.push({ side: 'top-right', rotate: 'rotate-45' });
      positions.push({ side: 'right', rotate: '' });
      positions.push({ side: 'bottom-right', rotate: '-rotate-45' });
      positions.push({ side: 'bottom', rotate: '' });
      positions.push({ side: 'bottom-left', rotate: '-rotate-45' });
      positions.push({ side: 'left', rotate: '' });
      positions.push({ side: 'top-left', rotate: 'rotate-45' });
    } else {
      // For capacity > 8, distribute evenly
      const angles = [0, 45, 90, 135, 180, 225, 270, 315];
      for (let i = 0; i < maxChairs; i++) {
        const angle = angles[i % angles.length];
        let side = '';
        let rotate = '';
        if (angle === 0) {
          side = 'top';
          rotate = '';
        } else if (angle === 45) {
          side = 'top-right';
          rotate = 'rotate-45';
        } else if (angle === 90) {
          side = 'right';
          rotate = '';
        } else if (angle === 135) {
          side = 'bottom-right';
          rotate = '-rotate-45';
        } else if (angle === 180) {
          side = 'bottom';
          rotate = '';
        } else if (angle === 225) {
          side = 'bottom-left';
          rotate = '-rotate-45';
        } else if (angle === 270) {
          side = 'left';
          rotate = '';
        } else if (angle === 315) {
          side = 'top-left';
          rotate = 'rotate-45';
        }
        positions.push({ side, rotate });
      }
    }

    // Position styles for chairs around the table
    const positionStyles = {
      top: 'absolute -top-4 left-1/2 transform -translate-x-1/2',
      'top-right': 'absolute -top-2 -right-4 transform translate-x-1/2 -translate-y-1/2',
      right: 'absolute -right-4 top-1/2 transform -translate-y-1/2',
      'bottom-right': 'absolute -bottom-2 -right-4 transform translate-x-1/2 translate-y-1/2',
      bottom: 'absolute -bottom-4 left-1/2 transform -translate-x-1/2',
      'bottom-left': 'absolute -bottom-2 -left-4 transform -translate-x-1/2 translate-y-1/2',
      left: 'absolute -left-4 top-1/2 transform -translate-y-1/2',
      'top-left': 'absolute -top-2 -left-4 transform -translate-x-1/2 -translate-y-1/2',
    };

    for (let i = 0; i < positions.length; i++) {
      const pos = positions[i];
      const chairColor =
        statusColor === 'available'
          ? 'text-green-600'
          : statusColor === 'reserved'
            ? 'text-red-600'
            : statusColor === 'occupied'
              ? 'text-gray-600'
              : 'text-gray-400';

      chairs.push(
        <div key={i} className={`${positionStyles[pos.side]} ${pos.rotate}`}>
          <FaChair className={`${chairColor} text-sm`} />
        </div>
      );
    }

    return chairs;
  };

  const getTableSize = (capacity) => {
    if (capacity <= 2) return 'w-20 h-20';
    if (capacity <= 4) return 'w-24 h-24';
    if (capacity <= 6) return 'w-28 h-28';
    if (capacity <= 8) return 'w-32 h-32';
    return 'w-36 h-36';
  };

  if (loading) {
    return (
      <div className="p-6 bg-gray-50 min-h-screen">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="w-12 h-12 border-2 border-black border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-500">Loading tables...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Your exact header style */}
      <header className="flex justify-between items-center mb-3 bg-white p-3">
        <h1 className="text-black text-xl font-bold ml-2">Food & Beverages Management</h1>
        <div className="flex space-x-6">
          <button
            onClick={() => navigate('/dashboard/core-modules/f&b-management/track-order-frontdesk')}
            className="btn "
          >
            Order Overview
          </button>
          <button
            onClick={() => navigate('/dashboard/core-modules/f&b-management/order-food')}
            className="btn"
          >
            Order Food
          </button>
          <button
            onClick={() => navigate('/dashboard/core-modules/f&b-management/dining-tables')}
            className="btn font-bold text-black"
          >
            Dining Tables
          </button>
          <button
            onClick={() => navigate('/dashboard/core-modules/f&b-management/track-order-kitchen')}
            className="btn"
          >
            Kitchen Display System(KDS)
          </button>
          <button
            onClick={() => navigate('/dashboard/core-modules/f&b-management/order-invoicing')}
            className="btn text-gray-600 hover:text-black"
          >
            Billing & Invoicing
          </button>
        </div>
      </header>

      {/* Page Title */}
      <div className="text-center mb-6 mt-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-1">Dining Tables Management</h1>
        <p className="text-sm font-poppins font-semibold text-center mb-1 text-gray-600">
          Manage table layouts, track real-time status, and optimize seating arrangements
        </p>
        <hr className="border border-gray-400 mt-1 w-64 mx-auto mb-7" />
      </div>

      {/* Stats Cards - Black and White */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg p-5 shadow-sm border border-gray-200">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-gray-500 text-sm">Total Tables</p>
              <p className="text-2xl font-bold text-black mt-1">{tables.length}</p>
            </div>
            <div className="bg-gray-100 p-3 rounded-lg">
              <FaTable className="text-gray-700 text-xl" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg p-5 shadow-sm border border-gray-200">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-gray-500 text-sm">Available</p>
              <p className="text-2xl font-bold text-green-600 mt-1">
                {tables.filter((t) => t.status === 'available').length}
              </p>
            </div>
            <div className="bg-green-100 p-3 rounded-lg">
              <FaCheckCircle className="text-green-600 text-xl" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg p-5 shadow-sm border border-gray-200">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-gray-500 text-sm">Occupied</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {tables.filter((t) => t.status === 'occupied').length}
              </p>
            </div>
            <div className="bg-gray-100 p-3 rounded-lg">
              <FaUsers className="text-gray-700 text-xl" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg p-5 shadow-sm border border-gray-200">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-gray-500 text-sm">Reserved</p>
              <p className="text-2xl font-bold text-red-600 mt-1">
                {tables.filter((t) => t.status === 'reserved').length}
              </p>
            </div>
            <div className="bg-red-100 p-3 rounded-lg">
              <FaClock className="text-red-600 text-xl" />
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-wrap gap-4 mb-6">
        <div className="flex-1 relative">
          <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm" />
          <input
            type="text"
            placeholder="Search by table number or name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-black bg-white"
          />
        </div>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-black bg-white text-gray-700"
        >
          <option value="all">All Status</option>
          <option value="available">Available</option>
          <option value="occupied">Occupied</option>
          <option value="reserved">Reserved</option>
          <option value="out_of_service">Out of Service</option>
        </select>
        <select
          value={filterSection}
          onChange={(e) => setFilterSection(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-black bg-white text-gray-700"
        >
          <option value="all">All Sections</option>
          {sections.map((section) => (
            <option key={section} value={section}>
              {section}
            </option>
          ))}
        </select>
        <button
          onClick={() => openModal()}
          className="px-5 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-all flex items-center gap-2"
        >
          <FaPlus size={12} /> Add Table
        </button>
      </div>

      {/* Tables Grid with Dynamic Chairs */}
      {Object.keys(groupedTables).length === 0 ? (
        <div className="bg-white rounded-lg p-12 text-center shadow-sm border border-gray-200">
          <FaTable className="text-5xl text-gray-300 mx-auto mb-4" />
          <p className="text-gray-400 text-lg">No tables found</p>
          <button
            onClick={() => openModal()}
            className="mt-4 px-6 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-all"
          >
            Add Your First Table
          </button>
        </div>
      ) : (
        Object.entries(groupedTables).map(([section, sectionTables]) => (
          <div key={section} className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <h2 className="text-lg font-semibold text-gray-800">{section}</h2>
              <div className="flex-1 h-px bg-gray-300"></div>
              <span className="text-xs text-gray-500">{sectionTables.length} tables</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {sectionTables.map((table) => {
                const StatusIcon = statusConfig[table.status]?.icon || FaCircle;
                const statusInfo = statusConfig[table.status] || statusConfig.available;
                const tableSize = getTableSize(table.capacity);

                return (
                  <div
                    key={table.id}
                    className={`bg-white rounded-lg shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden border-2 ${
                      table.status === 'occupied'
                        ? 'border-gray-500'
                        : table.status === 'reserved'
                          ? 'border-red-500'
                          : table.status === 'available'
                            ? 'border-green-500'
                            : 'border-gray-300'
                    }`}
                  >
                    {/* Status Bar */}
                    <div
                      className={`h-1 w-full ${
                        table.status === 'occupied'
                          ? 'bg-gray-700'
                          : table.status === 'reserved'
                            ? 'bg-red-500'
                            : table.status === 'available'
                              ? 'bg-green-500'
                              : 'bg-gray-300'
                      }`}
                    />

                    {/* Table Visual with Dynamic Chairs */}
                    <div className="flex justify-center pt-8 pb-8 bg-gray-50">
                      <div className={`${tableSize} relative`}>
                        <div
                          className={`w-full h-full rounded-xl flex items-center justify-center shadow-sm border-2 ${
                            table.status === 'occupied'
                              ? 'bg-white border-gray-400'
                              : table.status === 'reserved'
                                ? 'bg-white border-red-400'
                                : table.status === 'available'
                                  ? 'bg-white border-green-400'
                                  : 'bg-gray-100 border-gray-200'
                          }`}
                        >
                          <div className="text-center">
                            <FaUtensils
                              className={`mx-auto text-xl mb-1 ${
                                table.status === 'occupied'
                                  ? 'text-gray-700'
                                  : table.status === 'reserved'
                                    ? 'text-red-600'
                                    : table.status === 'available'
                                      ? 'text-green-600'
                                      : 'text-gray-400'
                              }`}
                            />
                            <span className="text-sm font-bold text-gray-800">
                              {table.table_number}
                            </span>
                            <div className="text-xs text-gray-500 mt-1">
                              {table.capacity} {table.capacity === 1 ? 'Guest' : 'Guests'}
                            </div>
                          </div>
                        </div>
                        {/* Render dynamic chairs based on capacity */}
                        {renderChairs(table.capacity, table.status)}
                      </div>
                    </div>

                    {/* Table Info */}
                    <div className="p-4 border-t border-gray-100">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <h3 className="font-semibold text-gray-800">{table.table_number}</h3>
                          {table.table_name && (
                            <p className="text-xs text-gray-500">{table.table_name}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-1 text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                          <FaUsers size={12} />
                          <span>{table.capacity}</span>
                        </div>
                      </div>

                      {/* Status Badge */}
                      <div className="mb-3">
                        <div
                          className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium ${statusInfo.badgeBg} ${statusInfo.badgeText}`}
                        >
                          <span
                            className={`w-1.5 h-1.5 rounded-full ${statusInfo.dotColor}`}
                          ></span>
                          <span>{statusInfo.label}</span>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex gap-2">
                        <select
                          value={table.status}
                          onChange={(e) => handleUpdateStatus(table.id, e.target.value)}
                          className="flex-1 px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:outline-none focus:border-black bg-white text-gray-700"
                        >
                          <option value="available">Set Available</option>
                          <option value="occupied">Set Occupied</option>
                          <option value="reserved">Set Reserved</option>
                          <option value="out_of_service">Out of Service</option>
                        </select>
                        <button
                          onClick={() => openModal(table)}
                          className="p-1.5 text-gray-500 hover:text-black transition-all border border-gray-300 rounded-md"
                        >
                          <FaEdit size={14} />
                        </button>
                        <button
                          onClick={() => handleDeleteTable(table.id, table.table_number)}
                          className="p-1.5 text-gray-500 hover:text-red-600 transition-all border border-gray-300 rounded-md"
                        >
                          <FaTrash size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))
      )}

      {/* Modal with Active Toggle - Label on Left, Toggle on Right */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full shadow-xl">
            <div className="flex justify-between items-center p-5 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-800">
                {editingTable ? 'Edit Table' : 'Add New Table'}
              </h2>
              <button onClick={closeModal} className="text-gray-400 hover:text-gray-600">
                <FaTimes size={20} />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Table Number *
                </label>
                <input
                  type="text"
                  value={formData.table_number}
                  onChange={(e) => setFormData({ ...formData, table_number: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:border-black"
                  placeholder="e.g., T1, Table 1, A1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Table Name</label>
                <input
                  type="text"
                  value={formData.table_name}
                  onChange={(e) => setFormData({ ...formData, table_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:border-black"
                  placeholder="e.g., Window Table, Corner Table"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Capacity (Number of Guests)
                </label>
                <input
                  type="number"
                  value={formData.capacity}
                  onChange={(e) => setFormData({ ...formData, capacity: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:border-black"
                  min="1"
                  max="20"
                />
                <p className="text-xs text-gray-400 mt-1">
                  The UI will show this many chairs around the table
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Section</label>
                <input
                  type="text"
                  value={formData.section}
                  onChange={(e) => setFormData({ ...formData, section: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:border-black"
                  placeholder="e.g., Indoor, Outdoor, VIP, Terrace"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:border-black bg-white"
                >
                  <option value="available">Available</option>
                  <option value="occupied">Occupied</option>
                  <option value="reserved">Reserved</option>
                  <option value="out_of_service">Out of Service</option>
                </select>
              </div>

              {/* Active Toggle - Label on Left, Toggle Switch on Right */}
              <div className="flex items-center justify-between py-2">
                <div>
                  <label className="text-sm font-medium text-gray-700">Table Status</label>
                  <p className="text-xs text-gray-400 mt-0.5">
                    Enable to make this table available for reservations
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, is_active: !formData.is_active })}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-300 focus:outline-none ${
                    formData.is_active ? 'bg-black' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform duration-300 ${
                      formData.is_active ? 'translate-x-6' : 'translate-x-0.5'
                    }`}
                  />
                </button>
              </div>
            </div>
            <div className="flex gap-3 p-5 border-t border-gray-200">
              <button
                onClick={closeModal}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-all"
              >
                Cancel
              </button>
              <button
                onClick={editingTable ? handleUpdateTable : handleCreateTable}
                className="flex-1 px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-all"
              >
                {editingTable ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer />
    </div>
  );
};

export default DiningTables;
